import * as angular from "angular";
import { CampaignDataProvider } from "./data-provider/campaign.dp";
import { DataSetDataProvider } from "./data-provider/data-set.dp";
import { EffortDataProvider } from "./data-provider/effort.dp";
import { DataProvider } from "./data-provider/data-provider";
import { SocketService } from "./env-set-up/socket";
import { FilterDataProvider } from "./data-provider/filter.dp";
import { Campaign } from "./models/campaign.model";
import { Filter } from "./models/filter.model";
import { Organisation } from "./models/organisation.model";
import { PrivacyNotice } from "./models/privacy-notice.model";
import { User } from "./models/user.model";
import { NavigationBarHelperService } from "./components/navigation/navigation-bar.helper";
import { NotificationDataProvider } from "./data-provider/notification.dp";
import { PermissionCheck } from "./shared/permission";
import { PrivacyNoticeDataProvider } from "./data-provider/privacy-notice.dp";
import { ApiUserService } from "./services/user.service";
import { CredentialsManager } from "./shared/credentials-manager";
import { UserDataProvider } from "./data-provider/user.dp";
import { DialogUtils } from "./helper/dialog.helper";
import { SearchUtils } from "./helper/search.helper";
import { Utils } from "./helper/utils.helper";
import { ToastUtils } from "./helper/toast.helper";
import { ScreenUtils } from "./helper/screen.helper";
import { WhiteLabelDataProvider } from "./data-provider/white-label.dp";
import * as Sentry from "@sentry/browser";
import { Angular as AngularIntegration } from "@sentry/integrations";
import { isSentryEnabled } from "./env-set-up/sentry-enabled";

declare let pendo: any;
let env = {};

//Import variables if present (from env.js)
if (window) {
    angular.extend(env, window["__env"]);
}

// Make sure to call Sentry.init after importing AngularJS.
// You can also pass {angular: AngularInstance} to the Integrations.Angular() constructor.
Sentry.init({
    dsn: window["__env"].sentry_dsn,
    enabled: isSentryEnabled(window["__env"]),
    integrations: [new AngularIntegration()],
    environment: window["__env"].deployment_type,
    // The deployed commit, appended to env.ts by JenkinsfileSinglePipeline (AV-18497); unset locally.
    release: window["__env"].sentry_release,
});

let app = angular.module("app", [
    "htmlTemplates",
    "ecanvasserControllers",
    "ecanvasserDirectives",
    "vcServices",
    "ngAnimate",
    "vcDataProvider",
    "filters",
    "ngCookies",
    "ngRoute",
    "ngSanitize",
    "ngResource",
    "ngMaterial",
    "ngSentry",
    "organisation",
    "socket",
    "dndLists",
    "profile",
    "md.data.table",
    "fixed.table.header",
    "notification",
    "credentialsManager",
    "requestInterceptor",
    "luegg.directives",
    "textAngular",
    "theme",
    "whiteLabelTheme",
]);

angular["lowercase"] = angular["$$lowercase"];

app.config([
    "$locationProvider",
    ($locationProvider: any) => {
        $locationProvider.hashPrefix("");
    },
]);
app.config(($httpProvider: any) => {
    angular["lowercase"] = (text: string) => (!text ? "" : text.toLowerCase());
    $httpProvider.defaults.withCredentials = true;
    $httpProvider.interceptors.push("httpRequestInterceptor");
});
app.config([
    "$qProvider",
    ($qProvider: any) => {
        $qProvider.errorOnUnhandledRejections(false);
    },
]);
app.config(($sceDelegateProvider: any, __env: any) => {
    $sceDelegateProvider.resourceUrlWhitelist([
        // Allow same origin resource loads.
        "self",
        // Allow loading from our assets domain.  Notice the difference between * and **.
        "https://assets." + __env.host + "/**",
    ]);
});
app.config([
    "$provide",
    ($provide: any) => {
        $provide.decorator("taOptions", [
            "$delegate",
            (taOptions: any) => {
                taOptions.forceTextAngularSanitize = true;
                taOptions.keyMappings = [];
                taOptions.toolbar = [
                    ["h1", "h2", "h3", "p", "quote"],
                    [
                        "bold",
                        "italics",
                        "underline",
                        "ul",
                        "ol",
                        "redo",
                        "undo",
                        "clear",
                    ],
                    [
                        "justifyLeft",
                        "justifyCenter",
                        "justifyRight",
                        "justifyFull",
                    ],
                ];
                taOptions.classes = {
                    focussed: "",
                    toolbar: "ta-toolbar",
                    toolbarGroup: "ta-button-group",
                    toolbarButton: "",
                    toolbarButtonActive: "active",
                    disabled: "disabled",
                    textEditor: "ta-text-editor",
                    htmlEditor: "md-input",
                };
                return taOptions;
            },
        ]);
        $provide.decorator("taTools", [
            "$delegate",
            (taTools: any) => {
                taTools.h1.display =
                    '<md-button aria-label="Heading 1">H1</md-button>';
                taTools.h2.display =
                    '<md-button aria-label="Heading 2">H2</md-button>';
                taTools.h3.display =
                    '<md-button aria-label="Heading 3">H3</md-button>';
                taTools.p.display =
                    '<md-button aria-label="Paragraph">P</md-button>';
                taTools.pre.display =
                    '<md-button aria-label="Pre">pre</md-button>';
                taTools.quote.display =
                    '<md-button class="md-icon-button" aria-label="Quote"><md-icon md-font-set="material-icons">format_quote</md-icon></md-button>';
                taTools.bold.display =
                    '<md-button class="md-icon-button" aria-label="Bold"><md-icon md-font-set="material-icons">format_bold</md-icon></md-button>';
                taTools.italics.display =
                    '<md-button class="md-icon-button" aria-label="Italic"><md-icon md-font-set="material-icons">format_italic</md-icon></md-button>';
                taTools.underline.display =
                    '<md-button class="md-icon-button" aria-label="Underline"><md-icon md-font-set="material-icons">format_underlined</md-icon></md-button>';
                taTools.ul.display =
                    '<md-button class="md-icon-button" aria-label="Buletted list"><md-icon md-font-set="material-icons">format_list_bulleted</md-icon></md-button>';
                taTools.ol.display =
                    '<md-button class="md-icon-button" aria-label="Numbered list"><md-icon md-font-set="material-icons">format_list_numbered</md-icon></md-button>';
                taTools.undo.display =
                    '<md-button class="md-icon-button" aria-label="Undo"><md-icon md-font-set="material-icons">undo</md-icon></md-button>';
                taTools.redo.display =
                    '<md-button class="md-icon-button" aria-label="Redo"><md-icon md-font-set="material-icons">redo</md-icon></md-button>';
                taTools.justifyLeft.display =
                    '<md-button class="md-icon-button" aria-label="Align left"><md-icon md-font-set="material-icons">format_align_left</md-icon></md-button>';
                taTools.justifyRight.display =
                    '<md-button class="md-icon-button" aria-label="Align right"><md-icon md-font-set="material-icons">format_align_right</md-icon></md-button>';
                taTools.justifyCenter.display =
                    '<md-button class="md-icon-button" aria-label="Align center"><md-icon md-font-set="material-icons">format_align_center</md-icon></md-button>';
                taTools.justifyFull.display =
                    '<md-button class="md-icon-button" aria-label="Justify"><md-icon md-font-set="material-icons">format_align_justify</md-icon></md-button>';
                taTools.clear.display =
                    '<md-button class="md-icon-button" aria-label="Clear formatting"><md-icon md-font-set="material-icons">format_clear</md-icon></md-button>';
                taTools.html.display =
                    '<md-button class="md-icon-button" aria-label="Show HTML"><md-icon md-font-set="material-icons">code</md-icon></md-button>';
                taTools.insertLink.display =
                    '<md-button class="md-icon-button" aria-label="Insert link"><md-icon md-font-set="material-icons">insert_link</md-icon></md-button>';
                taTools.insertImage.display =
                    '<md-button class="md-icon-button" aria-label="Insert photo"><md-icon md-font-set="material-icons">insert_photo</md-icon></md-button>';
                return taTools;
            },
        ]);
    },
]);

// Register environment in AngularJS as constant
app.constant("__env", env);
app.constant("__app_type", "USER");
app.constant("__time_zone", Intl.DateTimeFormat().resolvedOptions().timeZone);

app.config([
    "$routeProvider",
    ($routeProvider: any) => {
        $routeProvider
            .when("/", {
                template:
                    '<vc-home-page flex class="layout-column"></vc-home-page>',
            })
            .when("/expired", {
                templateUrl:
                    "/angular/components/campaign-plan/expired.component.html",
            })
            .when("/declined", {
                template:
                    '<vc-declined-privacy-notice flex class="layout-column"></vc-declined-privacy-notice>',
            })
            .when("/no_access", {
                templateUrl:
                    "/angular/components/request-access/no-access.component.html",
                controller: "RequestAccessController as $ctrl",
            })
            .when("/shared_revoked", {
                templateUrl:
                    "/angular/components/request-access/shared-revoked.component.html",
                controller: "RequestAccessController as $ctrl",
            })
            .when("/confirmpayment", {
                template:
                    '<vc-confirm-payment flex class="layout-column"></vc-confirm-payment>',
            })
            .when("/createcampaign", {
                template:
                    '<vc-create-campaign flex class="layout-column"></vc-create-campaign>',
            })
            .when("/voterfiles", {
                template:
                    '<vc-voter-file-list flex class="layout-column"></vc-voter-file-list>',
            })
            .when("/voterfiles/conflicts", {
                template:
                    '<vc-voter-file-conflict flex class="layout-column"></vc-voter-file-conflict>',
            })
            .when("/voterfiles/l2", {
                template:
                    '<vc-voter-file-l2 flex class="layout-column"></vc-voter-file-l2>',
            })
            .when("/voterfiles/import/:voterFileId", {
                template:
                    '<vc-voter-file-import flex class="layout-column"></vc-voter-file-import>',
            })
            .when("/voterfiles/import/:voterFileId/:imid", {
                template:
                    '<vc-voter-file-import flex class="layout-column"></vc-voter-file-import>',
            })
            .when("/voterfiles/info/:erid", {
                template:
                    '<vc-voter-file-import-info flex class="layout-column"></vc-import-info>',
            })
            .when("/voterfiles/info/:erid/:imid", {
                template:
                    '<vc-voter-file-import-info flex class="layout-column"></vc-import-info>',
            })
            .when("/voterfiles/skipped/:imid", {
                template:
                    '<vc-import-skipped flex class="layout-column"></vc-import-skipped>',
            })
            .when("/choose_import/", {
                template:
                    '<vc-choose-import flex class="layout-column"></vc-choose-import>',
            })
            .when("/profile", {
                reloadOnSearch: false,
                template:
                    '<vc-profile user="$ctrl.user" change-live-campaign="$ctrl.changeLiveCampaign(campaign, redirect)" flex class="layout-column"></vc-profile>',
            })
            .when("/cancel", {
                reloadOnSearch: false,
                template: '<cancel flex class="layout-column"></cancel>',
            })
            .when("/cancel/:campaignid", {
                reloadOnSearch: false,
                template: '<cancel flex class="layout-column"></cancel>',
            })
            .when("/essentials", {
                templateUrl:
                    "/angular/components/essentials/essentials.component.html",
            })
            .when("/people", {
                reloadOnSearch: false,
                template:
                    '<vc-house-occupants-view double-toolbar="$ctrl.doubleToolbar" flex class="layout-column"></vc-house-occupants-view>',
            })
            .when("/people/duplicates", {
                template:
                    '<vc-duplicates flex class="layout-column"></vc-duplicates>',
            })
            .when("/houses", {
                reloadOnSearch: false,
                template:
                    '<vc-houses-view double-toolbar="$ctrl.doubleToolbar" flex class="layout-column"></vc-houses-view>',
            })
            .when("/streets", {
                reloadOnSearch: false,
                template:
                    '<vc-streets-view double-toolbar="$ctrl.doubleToolbar" flex class="layout-column"></vc-streets-view>',
            })
            .when("/lists", {
                reloadOnSearch: false,
                template:
                    '<vc-groups-view double-toolbar="$ctrl.doubleToolbar" flex class="layout-column"></vc-groups-view>',
            })
            .when("/map", {
                reloadOnSearch: false,
                template: '<vc-map flex class="layout-column"></vc-map>',
            })
            .when("/canvasser/activity", {
                reloadOnSearch: false,
                template:
                    '<vc-canvasser-tracking flex class="layout-column"></vc-canvasser-tracking>',
            })
            .when("/teams", {
                reloadOnSearch: false,
                templateUrl:
                    "/angular/components/team/teams-view.component.html",
                controller: "TeamController as $ctrl",
            })
            .when("/users", {
                reloadOnSearch: false,
                templateUrl:
                    "/angular/components/user/users-view.component.html",
                controller: "TeamController as $ctrl",
            })
            .when("/invites", {
                reloadOnSearch: false,
                templateUrl:
                    "/angular/components/invite/invites-view.component.html",
                controller: "TeamController as $ctrl",
            })
            .when("/user_map", {
                reloadOnSearch: false,
                templateUrl:
                    "/angular/components/user-map/user-map-view.component.html",
                controller: "TeamController as $ctrl",
            })
            .when("/user/:userId", {
                reloadOnSearch: false,
                template:
                    '<vc-user-info flex class="layout-column"></vc-user-info>',
            })
            .when("/campaign/:campaignid", {
                reloadOnSearch: false,
                template:
                    '<vc-campaign-info flex class="layout-column"></vc-campaign-info>',
            })
            .when("/profile/campaign/:campaignid", {
                reloadOnSearch: false,
                template:
                    '<vc-campaign-info flex class="layout-column"></vc-campaign-info>',
            })
            .when("/authorize/app/", {
                reloadOnSearch: false,
                template:
                    '<vc-consent flex class="layout-column"></vc-consent>',
            })
            .when("/team/:tid", {
                reloadOnSearch: false,
                template:
                    '<vc-team-info flex class="layout-column"></vc-team-info>',
            })
            .when("/teams/scoreboard/:tid", {
                reloadOnSearch: false,
                template:
                    '<vc-team-score-board flex class="layout-column"></vc-team-score-board>',
            })
            .when("/talkingpoint", {
                template:
                    '<vc-talking-points flex class="layout-column"></vc-talking-points>',
            })
            .when("/results", {
                reloadOnSearch: false,
                template:
                    '<vc-results-page flex class="layout-column"></vc-results-page>',
            })
            .when("/results/efforts", {
                reloadOnSearch: false,
                template:
                    '<vc-grouped-stats-page flex class="layout-column"></vc-grouped-stats-page>',
            })
            .when("/results/survey/:sid", {
                reloadOnSearch: false,
                template:
                    '<vc-survey-results-page flex class="layout-column"></vc-survey-results-page>',
            })
            .when("/issues", {
                reloadOnSearch: false,
                template: '<vc-issues flex class="layout-column"></vc-issues>',
            })
            .when("/issues/:csqid", {
                reloadOnSearch: false,
                template: '<vc-issues flex class="layout-column"></vc-issues>',
            })
            .when("/exports", {
                reloadOnSearch: false,
                template:
                    '<vc-documents flex class="layout-column"></vc-documents>',
            })
            .when("/nationbuilder", {
                template:
                    '<vc-nation-builder flex class="layout-column"></vc-nation-builder>',
            })
            .when("/customize", {
                template:
                    '<vc-customize layout="column" flex layout-align="center center"></vc-customize>',
            })
            .when("/survey", {
                template:
                    '<vc-survey-list layout="column" flex></vc-survey-list>',
            })
            .when("/survey/:surveyId", {
                template:
                    '<vc-survey-info layout="column" flex></vc-survey-info>',
            })
            .when("/integration", {
                template:
                    '<vc-integration-list layout="column" flex></vc-integration-list>',
            })
            .when("/integration/nationbuilder", {
                redirectTo: "/nationbuilder",
            })
            .when("/integration/api", {
                templateUrl:
                    "/angular/components/integration/integration-api.component.html",
                controller: "IntegrationController as $ctrl",
            })
            .when("/integration/zapier", {
                templateUrl:
                    "/angular/components/integration/integration-zapier.component.html",
                controller: "IntegrationController as $ctrl",
            })
            .when("/integration/salesforce", {
                templateUrl:
                    "/angular/components/integration/integration-sales-force.component.html",
                controller: "IntegrationController as $ctrl",
            })
            .when("/integration/:integrationKey", {
                templateUrl:
                    "/angular/components/integration/integration.component.html",
                controller: "IntegrationController as $ctrl",
            })
            .when("/integration/:integrationKey/login", {
                template:
                    '<vc-integration-login flex class="layout-column"></vc-integration-login>',
            })
            .when("/privacy", {
                reloadOnSearch: false,
                template:
                    '<vc-privacy-dashboard flex class="layout-column"></vc-privacy-dashboard>',
            })
            .when("/logs", {
                template: '<vc-logs flex class="layout-column"></vc-logs>',
            })
            .when("/appointments", {
                template:
                    '<vc-appointments flex class="layout-column"></vc-appointments>',
            })
            .when("/events", {
                template: '<vc-events flex class="layout-column"></vc-events>',
            })
            .when("/events/info/:id", {
                template:
                    '<vc-event-info flex class="layout-column"></vc-event-info>',
            })
            .when("/events/create", {
                template:
                    '<vc-create-event flex class="layout-column"></vc-create-event>',
            })
            .when("/events/update/:id", {
                template:
                    '<vc-create-event flex class="layout-column"></vc-create-event>',
            })
            .when("/assignments", {
                template:
                    '<vc-assignment-chart flex class="layout-column"></vc-assignment-chart>',
            })
            .when("/public_api", {
                template:
                    '<vc-public-api flex class="layout-column"></vc-public-api>',
            })
            .when("/integration/:integrationKey/errors", {
                template:
                    '<vc-integration-errors flex class="layout-column"></vc-integration-errors>',
            })
            .when("/accountdetails", {
                template:
                    '<vc-plan-details-page flex class="layout-column"></vc-plan-details-page>',
            })
            .when("/welcome", {
                template:
                    '<vc-welcome-steps flex class="layout-column"></vc-welcome-steps>',
            })
            .when("/getting-started", {
                template:
                    '<vc-getting-started flex class="layout-column"></vc-getting-started>',
            });
    },
]).run(
    (
        $rootScope: any,
        $location: any,
        $window: any,
        __env: any,
        $mdMedia: any,
        NavigationBarHelperService: NavigationBarHelperService,
        FilterDataProvider: FilterDataProvider,
        SearchUtils: SearchUtils,
        DialogUtils: DialogUtils,
        $mdToast: any,
        ToastUtils: ToastUtils
    ) => {
        const hideNavMenuItemsPages = [
            "/authorize",
            "/cancel",
            "/essentials",
            "/welcome",
            "/expired",
            "/confirmpayment",
            "/declined",
            "/no_access",
            "/shared_revoked",
        ];
        const doubleToolbarPages = [
            "/cancel",
            "/welcome",
            "/expired",
            "/confirmpayment",
        ];

        const hideProfileMenuPages = ["/cancel", "/welcome"];
        const hideSideBarPages = [
            "/cancel",
            "/essentials",
            "/welcome",
            "/getting-started",
            "/expired",
            "/confirmpayment",
            "/declined",
            "/no_access",
            "/shared_revoked",
        ];
        const backButtonPages = ["/cancel"];

        $window.ga("create", __env.ga_key, "auto", { allowLinker: true });
        $window.ga("require", "linker");
        $window.ga("linker:autoLink", __env.ga_linked_domains);

        NavigationBarHelperService.setSideBarOpen($mdMedia("gt-sm"));
        // track pageview on state change

        let previousUrl: string;

        $rootScope.$on(
            "$locationChangeStart",
            ($event: any, newUrl: string, oldUrl: string) => {
                if (previousUrl && previousUrl === newUrl) {
                    if (
                        angular
                            .element(document.body)
                            .hasClass("md-dialog-is-showing")
                    ) {
                        $event.preventDefault(); // Prevent route from changing
                        DialogUtils.cancel(); // Cancel the active dialog
                    }
                }
                previousUrl = oldUrl;
                if (previousUrl.includes("/#/map")) {
                    ToastUtils.hide(); // hide geocoding snackbar
                }

                $rootScope.previousUrl = previousUrl;
            }
        );

        $rootScope.$on("$routeChangeSuccess", (event: any) => {
            $window.ga("send", "pageview", $location.path());
            NavigationBarHelperService.setNavigationBarHidden(false);

            NavigationBarHelperService.setSideBarOpen($mdMedia("gt-sm"));
            NavigationBarHelperService.setSetBarHidden(
                hideSideBarPages.indexOf($location.path()) > -1 ||
                    $location.path().indexOf("cancel") > -1
            );
            NavigationBarHelperService.setShowBackButton(
                backButtonPages.indexOf($location.path()) > -1 ||
                    $location.path().indexOf("cancel") > -1
            );
            NavigationBarHelperService.setMenuItemsHidden(
                hideNavMenuItemsPages.indexOf($location.path()) > -1 ||
                    $location.path().indexOf("cancel") > -1
            );
            NavigationBarHelperService.setProfileMenuHidden(
                hideProfileMenuPages.indexOf($location.path()) > -1 ||
                    $location.path().indexOf("cancel") > -1
            );
            NavigationBarHelperService.setSelectedSideBarItem($location.path());
            NavigationBarHelperService.toggleFilterButton(false);
            NavigationBarHelperService.filterOffset = false;
            NavigationBarHelperService.setSearchMode(false);
            SearchUtils.setSearchBar("", null, null);

            let mainElem = document.getElementsByClassName(
                "main-content-section"
            );
            let header = document.querySelector(".main-navbar");

            if (mainElem && mainElem[0]) {
                header.classList.remove("floatingNav");
            }

            FilterDataProvider.filter = new Filter();

            if (angular.isFunction($rootScope.setDoubleToolbar)) {
                $rootScope.setDoubleToolbar(
                    doubleToolbarPages.indexOf($location.path()) > -1 ||
                        $location.path().indexOf("cancel") > -1
                );
            }
        });
    }
);

export class RootController {
    private readonly $rootScope: any;
    private readonly $scope: any;
    private readonly $cookies: any;
    private readonly $window: any;
    private readonly $location: any;
    private readonly $mdMedia: any;

    private __env: any;
    private readonly modelsUtils: any;
    public searchUtils: SearchUtils;
    private readonly utils: Utils;

    private readonly credentialsManager: CredentialsManager;
    private readonly socketService: SocketService;
    private readonly dataProvider: DataProvider;
    private readonly userDataProvider: UserDataProvider;
    private readonly apiUserService: ApiUserService;
    private readonly effortDataProvider: EffortDataProvider;
    private readonly dialog: DialogUtils;
    private readonly privacyNoticeDataProvider: PrivacyNoticeDataProvider;

    public readonly notificationDataProvider: NotificationDataProvider;
    public readonly filterDataProvider: FilterDataProvider;
    public readonly permissionProvider: PermissionCheck;
    public readonly navigationBarService: NavigationBarHelperService;
    public readonly campaignDataProvider: CampaignDataProvider;
    public readonly dataSetDataProvider: DataSetDataProvider;

    public access_token: string;
    public intercomShowing = true;
    public activeCampaign: Campaign;
    public user: User;
    public screen: ScreenUtils;
    public privacyNotice: PrivacyNotice;
    public socket: any;
    public medScreen: any;
    public medToLargeScreen: any;
    public smallScreen: any;
    public doubleToolbar: boolean;
    public filterBarOpen: boolean;
    public showNotificationMenu = false;
    public alreadySignedIn: number;
    public profileMenuOpen = false;
    public campaignSwitching = false;
    public path: string;
    public params: any;
    public selectedOrganisation: Organisation;
    public subMenu = {
        showPlan: true,
        showOrganise: true,
        showEngage: true,
    };
    public whiteLabelDataProvider: WhiteLabelDataProvider;

    constructor(
        $rootScope: any,
        $scope: any,
        $cookies: any,
        $window: any,
        $location: any,
        __env: any,
        CredentialsManager: CredentialsManager,
        NotificationDataProvider: NotificationDataProvider,
        SocketService: SocketService,
        DataProvider: DataProvider,
        UserDataProvider: UserDataProvider,
        ApiUserService: ApiUserService,
        PermissionCheck: PermissionCheck,
        $mdMedia: any,
        FilterDataProvider: FilterDataProvider,
        NavigationBarHelperService: NavigationBarHelperService,
        WhiteLabelDataProvider: WhiteLabelDataProvider,
        CampaignDataProvider: CampaignDataProvider,
        EffortDataProvider: EffortDataProvider,
        DialogUtils: DialogUtils,
        DataSetDataProvider: DataSetDataProvider,
        PrivacyNoticeDataProvider: PrivacyNoticeDataProvider,
        ModelsUtils: any,
        ScreenUtils: ScreenUtils,
        SearchUtils: SearchUtils,
        Utils: Utils
    ) {
        this.$rootScope = $rootScope;
        this.$scope = $scope;
        this.$cookies = $cookies;
        this.$window = $window;
        this.$location = $location;
        this.__env = __env;
        this.credentialsManager = CredentialsManager;
        this.notificationDataProvider = NotificationDataProvider;
        this.socketService = SocketService;
        this.screen = ScreenUtils;
        this.dataProvider = DataProvider;
        this.userDataProvider = UserDataProvider;
        this.apiUserService = ApiUserService;
        this.permissionProvider = PermissionCheck;
        this.$mdMedia = $mdMedia;
        this.filterDataProvider = FilterDataProvider;
        this.navigationBarService = NavigationBarHelperService;
        this.campaignDataProvider = CampaignDataProvider;
        this.effortDataProvider = EffortDataProvider;
        this.dialog = DialogUtils;
        this.dataSetDataProvider = DataSetDataProvider;
        this.privacyNoticeDataProvider = PrivacyNoticeDataProvider;
        this.modelsUtils = ModelsUtils;
        this.searchUtils = SearchUtils;
        this.utils = Utils;
        this.whiteLabelDataProvider = WhiteLabelDataProvider;

        if (!this.$cookies.get("uid") || !this.$cookies.get("campaign")) {
            window.location.href = "/logout.php?r=" + this.$location.url();
        }
        if (this.$cookies.get("showPlan") == "false") {
            this.subMenu["showPlan"] = false;
        }
        if (this.$cookies.get("showOrganise") == "false") {
            this.subMenu["showOrganise"] = false;
        }
        if (this.$cookies.get("showEngage") == "false") {
            this.subMenu["showEngage"] = false;
        }
        if (
            parseFloat(this.__env.terms_conditions_version) >
                parseFloat(this.$cookies.get("termsconditionsversion")) ||
            angular.isUndefined(this.$cookies.get("termsconditionsversion"))
        ) {
            this.acceptTermsConditionsModal();
        }
        this.access_token = this.credentialsManager.getAccessToken();
        this.activeCampaign = this.campaignDataProvider.setActiveCampaign(
            this.$cookies.getObject("campaign")
        );

        this.user = this.modelsUtils.user({
            uid: this.$cookies.get("uid"),
            ufname:
                this.$cookies.get("ufname") == undefined
                    ? ""
                    : decodeURIComponent(
                          this.$cookies.get("ufname").replace(/\+/g, " ")
                      ),
            ulname:
                this.$cookies.get("ulname") == undefined
                    ? ""
                    : decodeURIComponent(
                          this.$cookies.get("ulname").replace(/\+/g, " ")
                      ),
            uemail: this.$cookies.get("uemail"),
            account_type: this.$cookies.get("account_type"),
            hash: this.$cookies.get("user_hash"),
        });

        this.campaignDataProvider.getOrganisations().then(() => {
            if (this.activeCampaign.paymentFailed() && !this.user.isAdmin()) {
                this.$window.location = "/#/confirmpayment";
            }
        });
        this.permissionProvider.setPermission(this.activeCampaign.permissionid);
        this.userDataProvider.setActiveUser(this.user);
        angular
            .element(document.querySelector(".main-content-section"))
            .bind("scroll", () => {
                alert("scrolling is cool!");
            });
        this.userDataProvider
            .getUser(this.$cookies.get("uid"))
            .then((user: User) => {
                user.card = this.user.card || {};
                angular.extend(this.user, user);
                this.userDataProvider.setActiveUser(this.user);
                this.notificationDataProvider.setUID(this.user.uid);
                this.permissionProvider.setBillingRight(
                    this.user.billing == "1" ||
                        this.user.account_type == "ADMIN"
                );
                this.initializeIntercomAndWootric();
                this.maybeShowAppDownloadPrompt();
            });

        this.permissionProvider.setPermission(this.activeCampaign.permissionid);

        this.notificationDataProvider.getUnseenIssuesCount();
        this.effortDataProvider.getAllEfforts();

        this.socketService.init(this.activeCampaign.campaignid, "campaign");

        this.credentialsManager.setCredentials(
            this.activeCampaign.campaignid,
            this.access_token,
            this.activeCampaign.oid,
            false,
            this.$cookies.get("uid")
        );

        this.$scope.$on("$destroy", () => {
            //this.socket.close();
        });

        this.$rootScope.setDoubleToolbar = (doubleToolbar: boolean) => {
            this.doubleToolbar = doubleToolbar;
        };

        this.$rootScope.setFilterBarOpen = (open: boolean) => {
            this.filterBarOpen = open;
        };

        this.$scope.$on("$mdMenuOpen", (event: any, elem: any) => {
            if (elem.hasClass("notification-menu")) {
                this.showNotificationMenu = true;
            }
        });

        this.alreadySignedIn = this.$cookies.get("signedIn") ? 1 : 0;

        this.$cookies.put("signedIn", "1", {
            domain: this.__env.host,
            path: "/",
        });

        this.path = this.$location.path();
        if (this.path != "/no_access" && this.path != "/shared_revoked") {
            setInterval(() => {
                this.checkIsActive();
            }, 60 * 1000); //check if user is active every 5 seconds
        }

        this.params = this.$location.search();
        if (this.params.campaignid) {
            this.campaignDataProvider
                .info(this.params.campaignid)
                .then((response: any) => {
                    this.changeLiveCampaign(
                        response.attributes,
                        this.$location.absUrl().split("?")[0]
                    );
                });
        }
    }

    $onInit() {
        this.$scope.$watch(
            () => this.$mdMedia("sm") || this.$mdMedia("xs"),
            (small: any) => (this.medScreen = small)
        );

        this.$scope.$watch(
            () => {
                return (
                    this.$mdMedia("sm") ||
                    this.$mdMedia("xs") ||
                    this.$mdMedia("md")
                );
            },
            (small: any) => (this.medToLargeScreen = small)
        );

        this.$scope.$watch(
            () => this.$mdMedia("xs"),
            (small: any) => (this.smallScreen = small)
        );

        this.utils.accessCheck();
        this.checkIsActive();
    }

    initializeIntercomAndWootric(): void {
        if (
            this.permissionProvider.hasPermission("INTERCOM") &&
            !this.__env.test &&
            !this.adminEmail(this.user.uemail) &&
            !this.user.uemail.includes("markaokeeffe+")
        ) {
            this.initializeIntercomUser();
            this.initializeWootric();
            //this.initializePendo();
        } else {
            this.intercomShowing = false;
        }
        this.initializeSentry();
    }

    // First-login nudge for QR self-signups to download the mobile app. Shown
    // once per user per browser (localStorage) and only on their first login.
    maybeShowAppDownloadPrompt(): void {
        if (!this.user || !this.user.is_qr_signup) {
            return;
        }
        const STORAGE_KEY = "ecanvasser_qr_app_promo_seen_" + this.user.uid;
        const logins = Number(this.user.logins);
        if (!Number.isFinite(logins) || logins > 1) {
            return;
        }

        let seen = false;
        try {
            seen = window.localStorage.getItem(STORAGE_KEY) === "1";
        } catch (e) {
            seen = false;
        }
        if (seen) {
            return;
        }
        try {
            window.localStorage.setItem(STORAGE_KEY, "1");
        } catch (e) {
            // Storage can throw in private mode; still show it this once.
        }

        this.dialog.show({
            component: "vc-qr-app-download",
            clickOutsideToClose: true,
            fullscreen: false,
            size: "small",
        });
    }

    openInviteDialog(ev: any): void {
        this.dialog
            .show({
                component: "vc-user-invite",
                targetEvent: ev,
                clickOutsideToClose: false,
                size: "large",
            })
            .then(() => this.userDataProvider.resetUsersCount());
    }

    acceptTermsConditionsModal($event?: any): void {
        const parentEl = angular.element(document.body);
        this.dialog.show({
            parent: parentEl,
            targetEvent: $event,
            component: "vc-user-terms-conditions",
            locals: {
                uid: this.user.uid,
            },
            clickOutsideToClose: false,
        });
    }

    testIntercomFab($event: any): void {
        const dialog = this.dialog
            .alert()
            .title("Dummy intercom bubble")
            .textContent("No intercom loaded on test server")
            .targetEvent($event)
            .ok("OK");
        this.dialog.showPreConfiguredDialog(dialog);
    }

    privacyNoticeCheck(): void {
        this.privacyNoticeDataProvider
            .pending()
            .then((privacyNotice?: PrivacyNotice) => {
                if (privacyNotice) {
                    this.acknowledgePrivacyNoticeDialog(privacyNotice);
                }
            });
    }

    acknowledgePrivacyNoticeDialog(
        privacyNotice: PrivacyNotice,
        $event?: any
    ): void {
        this.privacyNotice = privacyNotice;
        this.utils.acknowledgePrivacyNoticeDialog(privacyNotice, $event);
    }

    navigateBack(): void {
        this.$window.history.back();
    }

    checkIsActive(): void {
        const type = this.alreadySignedIn ? 0 : 1;
        this.apiUserService.is_active({ type: type }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    if (this.$cookies.get("noAccess") == "true") {
                        this.$cookies.remove("noAccess");
                        this.$location.path("/");
                    } else {
                        this.privacyNoticeCheck();
                    }
                } else {
                    this.$cookies.put("noAccess", "true");

                    if (this.alreadySignedIn) {
                        this.$location.path("/shared_revoked");
                    } else {
                        this.$location.path("/no_access");
                    }
                }
                this.alreadySignedIn = 1; // only ever try to auto assign license on first request
            },
            () => {
                window.location.href = "/logout.php?r=" + this.$location.url();
            }
        );
    }

    openProfileMenu(): void {
        this.setSelectedOrganisation(null);
        this.profileMenuOpen = true;
        const removeListener = this.$scope.$on("$mdMenuClose", () => {
            this.profileMenuOpen = false;
            removeListener();
        });
    }

    setSelectedOrganisation(organisation: Organisation): void {
        this.selectedOrganisation = organisation;
    }

    changeLiveCampaign(campaign: Campaign, redirect?: string): void {
        this.campaignSwitching = true;
        this.$cookies.remove("privacyNotice");
        if (campaign.campaignid != this.activeCampaign.campaignid) {
            this.$cookies.remove("noAccess");
        }
        this.campaignDataProvider
            .changeLiveCampaign(campaign)
            .then((returnedCampaign: Campaign) => {
                this.initNewCampaignData(returnedCampaign);

                this.userDataProvider
                    .getUser(this.$cookies.get("uid"))
                    .then((user: User) => {
                        user.card = this.user.card || {};
                        angular.extend(this.user, user);
                        this.userDataProvider.setActiveUser(this.user);
                        this.notificationDataProvider.setUID(this.user.uid);
                        this.permissionProvider.setBillingRight(
                            this.user.billing == "1" ||
                                this.user.account_type == "ADMIN"
                        );
                        this.initializeIntercomAndWootric();

                        if (redirect || returnedCampaign.redirect) {
                            this.$window.location =
                                redirect ?? returnedCampaign.redirect;
                            setTimeout(() => {
                                this.$window.location.reload();
                                this.campaignSwitching = false;
                            }, 500);
                        } else {
                            this.$window.location.reload();
                            setTimeout(() => {
                                this.campaignSwitching = false;
                            }, 200);
                        }
                    });
            });
    }

    initNewCampaignData(campaign: Campaign): void {
        this.activeCampaign = campaign;
        this.socketService.changeTopic(campaign.campaignid, "campaign");
        this.dataProvider.clearAll();
        this.permissionProvider.setPermission(campaign.permissionid);
        this.effortDataProvider.getAllEfforts();
    }

    switchLeaderView(organisation: Organisation): void {
        if (
            organisation &&
            organisation != this.campaignDataProvider.activeOrganisation
        ) {
            this.campaignDataProvider
                .changeLiveCampaign(organisation.campaigns[0])
                .then((campaign: Campaign) => {
                    this.socketService.changeTopic(
                        campaign.campaignid,
                        "campaign"
                    );
                    this.dataProvider.clearAll();
                    this.permissionProvider.setPermission(
                        campaign.permissionid
                    );
                    this.$window.location = "/leader.php/#/divisions";
                });
        } else {
            this.$window.location = "/leader.php/#/divisions";
        }
    }

    runWootric(): void {
        if (this.$window.wootricSettings) {
            const url = this.$location.url();
            const pageName = url.split("?")[0].substring(1);
            this.$window.wootricSettings.properties.page = pageName ?? null;
            this.$window.wootric("run");
        }
    }

    adminEmail(email: string): boolean {
        return (
            email &&
            (email.includes("@vconnecta") ||
                email.includes("@civisconnect") ||
                email.includes("@voxcitio") ||
                email.includes("@ecanvasser"))
        );
    }

    initializeWootric(): void {
        this.$window.wootricSettings = {
            email: this.user.uemail, //TODO: Required to uniquely identify a user. Email is recommended but this can be any unique identifier.
            external_id: this.user.uid, // TODO: Reference field for external integrations only. Send it along with email.
            time_delay: 1,
            product_name: this.__env.product_name,
            facebook_page: "https://www.facebook.com/" + this.__env.social,
            twitter_account: this.__env.social,
            account_token: this.__env.wootric_token, //TODO:replace it with your account token
            properties: {
                account_name: this.activeCampaign.campaignname,
                account_id: this.activeCampaign.campaignid,
                leader_id: this.activeCampaign.oid,
                permission: this.user.permissionname,
                planLevel: this.activeCampaign.cpname,
                planPrice: this.activeCampaign.cpprice,
                account_status: this.activeCampaign.campaignstatusname,
                account_created: this.activeCampaign.campaigncreationdatetime,
            },
        };
    }

    initializeSentry(): void {
        Sentry.setUser({
            id: this.user.uid,
            email: this.user.uemail,
        });
        Sentry.setTag("campaign_id", this.activeCampaign.campaignid);
        Sentry.setTag("environment", this.__env.deployment_type);
        Sentry.setContext("campaign", this.activeCampaign);
    }

    initializePendo(): void {
        pendo.initialize({
            visitor: {
                id: this.user.uid,
                email: this.user.uemail,
                full_name: this.user.ufname + " " + this.user.ulname,
                role: this.user.permissionname,
                user_hash: this.$cookies.get("user_hash"),
                join_date: this.$cookies.get("ujoindate"),
            },
            account: {
                id: this.activeCampaign.campaignid,
                oid: this.activeCampaign.oid,
                name: this.activeCampaign.campaignname,
                region: this.activeCampaign.campaignregion,
                status: this.activeCampaign.campaignstatusname,
                plan_country: this.activeCampaign.cpcountry,
                interval: this.activeCampaign.cpinterval,
                planLevel: this.activeCampaign.cpname,
                planPrice: this.activeCampaign.cpprice,
                nb_nation: this.activeCampaign.nbnid,
                nb_slug: this.activeCampaign.slug,
                creationDate: this.activeCampaign.campaigntimestamp,
            },
        });
    }

    initializeIntercomUser(): void {
        this.campaignDataProvider.initializeIntercomUser();
    }

    toggleSubMenu(subMenuName: string): void {
        this.subMenu[subMenuName] = !this.subMenu[subMenuName];
        this.$cookies.put(subMenuName, `${this.subMenu[subMenuName]}`);
    }
}

app.controller("RootController", RootController);
